import { AxiosResponse } from "axios";
import { useEffect } from "react";
import { createRef, useContext, useRef, useState } from "react";
import { AdminFrame } from "views/common/AdminFrame";
import { delApiDataWithToken, postApiDataWithToken } from "views/utils/CallApi";
import { getRef, getRefCheckbox, setRef, setRefCheckbox, setRefDisabled } from "../../../utils/InputHelp";
import { CtxAuth } from "views/utils/Contexts";
import * as mb from "views/common/MessageBox";
import { Dialog } from "views/common/Dialog";
import { formatTime } from "views/utils/CommonFunc";
import * as Validation from "../../../utils/Validation";
import { InputDate } from "views/common/InputDate";

interface rowData {
    monday_flg: number,
    tuesday_flg: number,
    wednesday_flg: number,
    thursday_flg: number,
    friday_flg: number,
    saturday_flg: number,
    sunday_flg: number,
    holiday_flg: number,
    frame_list:{
        schedule_start_time: string,
        schedule_end_time: string
    }[]
}

interface rowRef {
    monday_flg: React.RefObject<HTMLInputElement>,
    tuesday_flg: React.RefObject<HTMLInputElement>,
    wednesday_flg: React.RefObject<HTMLInputElement>,
    thursday_flg: React.RefObject<HTMLInputElement>,
    friday_flg: React.RefObject<HTMLInputElement>,
    saturday_flg: React.RefObject<HTMLInputElement>,
    sunday_flg: React.RefObject<HTMLInputElement>,
    holiday_flg: React.RefObject<HTMLInputElement>,
    frame_list: {
        schedule_start_time: React.RefObject<HTMLInputElement>,
        schedule_end_time: React.RefObject<HTMLInputElement>,
    }[]
}

interface IFFrameData {
    frame_no: number,
    schedule_start_time: string,
    schedule_end_time: string
}

interface IFFrameRef {
    schedule_start_time: React.RefObject<HTMLInputElement>,
    schedule_end_time: React.RefObject<HTMLInputElement>
}

export function ScheduleEdit(): JSX.Element{

    const cua = useContext(CtxAuth);
    const params = new URL(window.location.href).searchParams.get("schedule");
    const [msgArgs, setMsgArgs] = useState({type:"", text:""});
    const [isShow, setIsShow] = useState<boolean>(false);
    const [submitDisabled, setSubmitDisabled] = useState<boolean>(true);
    const startDate = useRef<HTMLInputElement>(null);
    const endDate = useRef<HTMLInputElement>(null);
    const [rowData, setRowData] = useState<rowData[]>([]);      //行の値保持用
    const [rowRef, setRowRef] = useState<rowRef[]>([]);         //行のコントロール保持用
    const [editControlsDisabled, setEditControlsDisalbed] = useState<boolean>(true);
    
    //-------------------------------------
    // 画面ロード時処理
    //-------------------------------------
    useEffect(function(){
        if (!params){
            //新規作成の場合
            clickAddRow();
            setSubmitDisabled(false);
            setEditControlsDisalbed(false);
        } else {
            //更新の場合
            postApiDataWithToken('/management/schedule/' + params,
                {},
                cua.getToken(),
                function(res:AxiosResponse<any>){
                    switch (res.status){
                        case 200:

                            //変更可能判定
                            let scheduleDate:Date = new Date(res.data.scheduled_date);
                            let sDate:Date = new Date(res.data.schedule_list[0].start_date);
                            if (sDate <= scheduleDate){
                                if (res.data.schedule_list[0].end_date){
                                    setMsgArgs({type:mb.MSGBOX_TYPE_ERROR, text:"このデータは編集できません。"});
                                    return;
                                }
                            } else {
                                setEditControlsDisalbed(false);
                            }

                            let tmpRowData:rowData[] = [];
                            let tmpRowRef:rowRef[] = [];

                            //曜日枠（パターン）
                            res.data.schedule_list[0].pattern_list.forEach(function(pattern:any){

                                //時間（フレーム）
                                let tmpFrameData:IFFrameData[] = [];
                                let tmpFrameRef:IFFrameRef[] = [];
                                pattern.frame_list.forEach(function(frame:any){
                                    tmpFrameData.push({
                                        frame_no: frame.frame_no,
                                        schedule_start_time: frame.schedule_start_time,
                                        schedule_end_time: frame.schedule_end_time
                                    });
                                    tmpFrameRef.push({
                                        schedule_start_time: createRef(),
                                        schedule_end_time: createRef()
                                    });
                                });

                                tmpRowData.push({
                                    monday_flg: pattern.monday_flg,
                                    tuesday_flg: pattern.tuesday_flg,
                                    wednesday_flg: pattern.wednesday_flg,
                                    thursday_flg: pattern.thursday_flg,
                                    friday_flg: pattern.friday_flg,
                                    saturday_flg: pattern.saturday_flg,
                                    sunday_flg: pattern.sunday_flg,
                                    holiday_flg: pattern.holiday_flg,
                                    frame_list: tmpFrameData,
                                });
                                tmpRowRef.push({
                                    monday_flg: createRef(),
                                    tuesday_flg: createRef(),
                                    wednesday_flg: createRef(),
                                    thursday_flg: createRef(),
                                    friday_flg: createRef(),
                                    saturday_flg: createRef(),
                                    sunday_flg: createRef(),
                                    holiday_flg: createRef(),
                                    frame_list: tmpFrameRef,
                                });

                            });

                            setRowRef(tmpRowRef);
                            setRowData(tmpRowData);
                            setRef(startDate, res.data.schedule_list[0].start_date.replaceAll("/", "-"));
                            if (res.data.schedule_list[0].end_date){
                                setRef(endDate, res.data.schedule_list[0].end_date.replaceAll("/", "-"));
                            }
                            setSubmitDisabled(false);
                            break;

                        default:
                            setMsgArgs({type:mb.MSGBOX_TYPE_ERROR, text:"画面のロードに失敗しました。"});
                    }
                }
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[]);

    //-------------------------------------
    // データ設定
    //-------------------------------------
    //1度目のレンダリングの後の処理で生成される入力項目 に値を設定するには
    //2回目のレンダリングされた後に処理する必要がある
    useEffect(function(){
        rowData.forEach(function(item, idx){
            setRefCheckbox(rowRef[idx].sunday_flg, getNumToBool(item.sunday_flg));
            setRefCheckbox(rowRef[idx].monday_flg, getNumToBool(item.monday_flg));
            setRefCheckbox(rowRef[idx].tuesday_flg, getNumToBool(item.tuesday_flg));
            setRefCheckbox(rowRef[idx].wednesday_flg, getNumToBool(item.wednesday_flg));
            setRefCheckbox(rowRef[idx].thursday_flg, getNumToBool(item.thursday_flg));
            setRefCheckbox(rowRef[idx].friday_flg, getNumToBool(item.friday_flg));
            setRefCheckbox(rowRef[idx].saturday_flg, getNumToBool(item.saturday_flg));
            setRefCheckbox(rowRef[idx].holiday_flg, getNumToBool(item.holiday_flg));
            item.frame_list.forEach(function(frame, fIdx){
                setRef(rowRef[idx].frame_list[fIdx].schedule_start_time, formatTime(frame.schedule_start_time));
                setRef(rowRef[idx].frame_list[fIdx].schedule_end_time, formatTime(frame.schedule_end_time));
            });
        });
    }, [rowData, rowRef]);

    //-------------------------------------
    // 行追加
    //-------------------------------------
    function clickAddRow(){
        setRowData([...getCurrentRowData(), {
            sunday_flg: 0,
            monday_flg: 0,
            tuesday_flg: 0,
            wednesday_flg: 0,
            thursday_flg: 0,
            friday_flg: 0,
            saturday_flg: 0,
            holiday_flg: 0, 
            frame_list: [{schedule_start_time: "00:00",schedule_end_time: "00:00"}]
            }]);
        setRowRef([...rowRef, {
            sunday_flg: createRef(),
            monday_flg: createRef(),
            tuesday_flg: createRef(),
            wednesday_flg: createRef(),
            thursday_flg: createRef(),
            friday_flg: createRef(),
            saturday_flg: createRef(),
            holiday_flg: createRef(),
            frame_list: [{schedule_start_time: createRef(), schedule_end_time: createRef()}]
        }]);
    }

    //-------------------------------------
    // 行削除
    //-------------------------------------
    function clickDelRow(rowNo:number){
        let tmpRowData:rowData[] = getCurrentRowData();
        let tmpRowRef:rowRef[] = rowRef.concat();
        tmpRowData.splice(rowNo, 1);
        tmpRowRef.splice(rowNo, 1);
        setRowData(tmpRowData);
        setRowRef(tmpRowRef);
    }

    //-------------------------------------
    // 時間行追加
    //-------------------------------------
    function clickAddTimeRow(rowNo:number){
        let tmpRowData:rowData[] = getCurrentRowData();
        let tmpRowRef:rowRef[] = rowRef.concat();
        tmpRowData[rowNo].frame_list.push({
            schedule_start_time: "00:00",
            schedule_end_time: "00:00"
        });
        tmpRowRef[rowNo].frame_list.push({
            schedule_start_time: createRef(),
            schedule_end_time: createRef()
        });
        setRowData(tmpRowData);
        setRowRef(tmpRowRef);
    }

    //-------------------------------------
    // 時間行削除
    //-------------------------------------
    function clickDelTimeRow(rowNo:number, timeNo:number){
        let tmpRowData:rowData[] = getCurrentRowData();
        let tmpRowRef:rowRef[] = rowRef.concat();
        tmpRowData[rowNo].frame_list.splice(timeNo, 1);
        tmpRowRef[rowNo].frame_list.splice(timeNo, 1);
        setRowData(tmpRowData);
        setRowRef(tmpRowRef);
    }

    //-------------------------------------
    // 画面上データの取得
    //-------------------------------------
    function getCurrentRowData():rowData[]{
        let tmpRowData:rowData[] = [];
        rowRef.forEach(function(item){
            let tmpFlame:any = [];
            item.frame_list.forEach(function(frame){
                tmpFlame.push({
                    schedule_start_time: frame.schedule_start_time.current?.value,
                    schedule_end_time: frame.schedule_end_time.current?.value
                })
            });
            tmpRowData.push({
                sunday_flg: getBoolToNum(getRefCheckbox(item.sunday_flg)),
                monday_flg: getBoolToNum(getRefCheckbox(item.monday_flg)),
                tuesday_flg: getBoolToNum(getRefCheckbox(item.tuesday_flg)),
                wednesday_flg: getBoolToNum(getRefCheckbox(item.wednesday_flg)),
                thursday_flg: getBoolToNum(getRefCheckbox(item.thursday_flg)),
                friday_flg: getBoolToNum(getRefCheckbox(item.friday_flg)),
                saturday_flg: getBoolToNum(getRefCheckbox(item.saturday_flg)),
                holiday_flg: getBoolToNum(getRefCheckbox(item.holiday_flg)),
                frame_list:tmpFlame
            });
        });
        return tmpRowData;
    }

    //-------------------------------------
    // 変更を保存
    //-------------------------------------
    function clickSave(){
        
        allControlDisabled(true);

        let tmpRowData:rowData[] = getCurrentRowData();
        
        // バリデーション
        let errmsg:string = "";
        setMsgArgs({type:mb.MSGBOX_TYPE_INFO, text:""});

        //開始日・終了日
        errmsg = Validation.checkEmptyTextBox(getRef(startDate), "適用開始日")
        if (errmsg){
            setMsgArgs({type:mb.MSGBOX_TYPE_ERROR, text:errmsg});
            allControlDisabled(false);
            return;
        }
        errmsg = Validation.checkReversalDate(new Date(getRef(startDate)), new Date(getRef(endDate)), "適用開始日", "適用終了日");
        if (errmsg){
            setMsgArgs({type:mb.MSGBOX_TYPE_ERROR, text:errmsg});
            allControlDisabled(false);
            return;
        }

        let daysBase = [0,0,0,0,0,0,0,0];
        let before_eTime = "";
        let errFlg = false;
        for (let schedule of tmpRowData){

            before_eTime = "";

            //曜日：一個も選択されていない場合エラー
            if (schedule.monday_flg === 0 && schedule.tuesday_flg === 0 && schedule.wednesday_flg === 0 && schedule.thursday_flg === 0 && 
                schedule.friday_flg === 0 && schedule.saturday_flg === 0 && schedule.sunday_flg === 0 && schedule.holiday_flg === 0 ){
                setMsgArgs({type:mb.MSGBOX_TYPE_ERROR, text:"一つ以上の曜日を選択してください。"});
                allControlDisabled(false);
                errFlg = true;
                break;
            }
            //曜日：重複している場合エラー
            let days = [schedule.monday_flg, schedule.tuesday_flg, schedule.wednesday_flg, schedule.thursday_flg, 
                schedule.friday_flg, schedule.saturday_flg, schedule.sunday_flg, schedule.holiday_flg];
            for (let i = 0; i < 8; i++){
                if (days[i] === 1){
                    if (daysBase[i] === 1) {
                        setMsgArgs({type:mb.MSGBOX_TYPE_ERROR, text:"選択されている曜日が重複しています。"});
                        allControlDisabled(false);
                        errFlg = true;
                    } else {
                        daysBase[i] = 1;
                    }
                }
            }
            if (errFlg){
                break;
            }

            //開始時間・終了時間
            for (let frame of schedule.frame_list){
                errmsg = Validation.checkReversalTime(frame.schedule_start_time, frame.schedule_end_time, "開始時間", "終了時間");
                if (errmsg){
                    setMsgArgs({type:mb.MSGBOX_TYPE_ERROR, text:errmsg});
                    allControlDisabled(false);
                    errFlg = true;
                    break;
                }
                errmsg = Validation.checkReversalTimeRow(frame.schedule_start_time, before_eTime, "開始時間", "終了時間");
                if (errmsg){
                    setMsgArgs({type:mb.MSGBOX_TYPE_ERROR, text:errmsg});
                    allControlDisabled(false);
                    errFlg = true;
                    break;
                }
                before_eTime = frame.schedule_end_time;
            };
            if (errFlg){
                break;
            }
            
        };
        if (errFlg){
            return;
        }

        // 保存
        if (!params){
            //新規登録の場合
            postApiDataWithToken('/management/schedule/register',
                {start_date:getRef(startDate), end_date:getRef(endDate), pattern_list:tmpRowData},
                cua.getToken(),
                function(res:AxiosResponse<any>){
                    switch (res.status){
                        case 200:
                            setMsgArgs({type:mb.MSGBOX_TYPE_SUCCESS, text:"基本営業時間の登録が完了しました。"});
                            break;
                        case 401:
                            setMsgArgs({type:mb.MSGBOX_TYPE_ERROR, text:"他の設定期間と重複しています。"});
                            break;
                        default:
                            setMsgArgs({type:mb.MSGBOX_TYPE_ERROR, text:"基本営業時間の登録に失敗しました。"});
                    }
                    allControlDisabled(false);
                }
            );
        } else {
            //更新の場合
            postApiDataWithToken('/management/schedule/change',
                {schedule_no:params, start_date:getRef(startDate), end_date:getRef(endDate), pattern_list:tmpRowData},
                cua.getToken(),
                function(res:AxiosResponse<any>){
                    switch (res.status){
                        case 200:
                            setMsgArgs({type:mb.MSGBOX_TYPE_SUCCESS, text:"基本営業時間の更新が完了しました。"});
                            break;
                        case 401:
                            setMsgArgs({type:mb.MSGBOX_TYPE_ERROR, text:"他の設定期間と重複しています。"});
                            break;
                        default:
                            setMsgArgs({type:mb.MSGBOX_TYPE_ERROR, text:"基本営業時間の更新に失敗しました。"});
                    }
                    allControlDisabled(false);
                }
            );
        }

    }

    //-------------------------------------
    //削除確認画面
    //-------------------------------------
    function clickOpen(){
        setIsShow(true);
    }

    //-------------------------------------
    // 削除
    //-------------------------------------
    function clickDelete(){
        setIsShow(false);
        allControlDisabled(true);
        delApiDataWithToken('/management/schedule/delete/' + params,
            {},
            cua.getToken(),
            function(res:AxiosResponse<any>){
                switch (res.status){
                    case 200:
                        setMsgArgs({type:mb.MSGBOX_TYPE_SUCCESS, text:"基本営業時間の削除が完了しました。"});
                        break;
                    default:
                        setMsgArgs({type:mb.MSGBOX_TYPE_ERROR, text:"基本営業時間の削除に失敗しました。"});
                        allControlDisabled(false);
                }
            }
        );
    }

    //-------------------------------------
    // 全コントロール非活性化
    //-------------------------------------
    function allControlDisabled(val:boolean){
        setSubmitDisabled(val);
        setEditControlsDisalbed(val);
        setRefDisabled(endDate, val);
    }

    //-------------------------------------
    // 値変換処理
    //-------------------------------------
    //数字からbooleanを返す
    function getNumToBool(val:number):boolean{
        if (val === 1){
            return true;
        } else {
            return false;
        }
    }
    //booleanから数字を返す
    function getBoolToNum(val:boolean):number {
        if (val === false){
            return 0;
        } else {
            return 1;
        }
    }

    return (
        <AdminFrame>
            <div className="contents">
                <div className="breadcrumb"><a href="list">← 一覧に戻る</a></div>
                <mb.MessageBox args={msgArgs} />
                <h2 className="settings-title-h2">スケジュール管理 &gt; 基本営業時間登録</h2>
                <div className="settings-wrap-short">
                    <div className="settings-block">
                        <h3 className="settings-title-h3">基本営業時間</h3>
                        <div className="settings-form">
                            <div className="form-row">
                                <div className="control-horizon schedule-edit-date-wrap">
                                <div className="schedule-edit-date">
                                    <div className="form-row-title">適用開始日</div>
                                    <div className="form-row-input"><InputDate refElm={startDate} disabled={editControlsDisabled}/></div>
                                </div>
                                <div className="schedule-edit-date">
                                    <div className="form-row-title">適用終了日</div>
                                    <div className="form-row-input"><InputDate refElm={endDate}/></div>
                                </div>
                                </div>
                            </div>
                            {rowRef.map(function(item, index){
                                return (
                                    <div key={index} className="schedule-detail-item">
                                        <div className="schedule-detail-item-day">
                                            <div className="schedule-detail-item-day-checkbox control-horizon">
                                                <label className="checkbox"><input type="checkbox" ref={item.monday_flg} disabled={editControlsDisabled}/><div>月</div></label>
                                                <label className="checkbox"><input type="checkbox" ref={item.tuesday_flg} disabled={editControlsDisabled}/><div>火</div></label>
                                                <label className="checkbox"><input type="checkbox" ref={item.wednesday_flg} disabled={editControlsDisabled}/><div>水</div></label>
                                                <label className="checkbox"><input type="checkbox" ref={item.thursday_flg} disabled={editControlsDisabled}/><div>木</div></label>
                                                <label className="checkbox"><input type="checkbox" ref={item.friday_flg} disabled={editControlsDisabled}/><div>金</div></label>
                                                <label className="checkbox"><input type="checkbox" ref={item.saturday_flg} disabled={editControlsDisabled}/><div>土</div></label>
                                                <label className="checkbox"><input type="checkbox" ref={item.sunday_flg} disabled={editControlsDisabled}/><div>日</div></label>
                                                <label className="checkbox"><input type="checkbox" ref={item.holiday_flg} disabled={editControlsDisabled}/><div>祝</div></label>
                                            </div>
                                        </div>
                                        <div className="schedule-detail-item-time">
                                            <ul className="schedule-detail-item-time-list">
                                                {item.frame_list.map(function(frame, idx){
                                                    return (
                                                        <li key={idx} className="control-horizon control-vcenter">
                                                            <div className="form-row-input"><input type="time" ref={frame.schedule_start_time} disabled={editControlsDisabled}/></div>
                                                            <div className="form-row-input"><input type="time" ref={frame.schedule_end_time} disabled={editControlsDisabled}/></div>
                                                            {item.frame_list.length > 1 &&
                                                                <button className="btn-row js-btn-del-time" onClick={function(){clickDelTimeRow(index,idx)}} disabled={editControlsDisabled}><span className="material-icons-round">clear</span></button>
                                                            }
                                                        </li>
                                                    )
                                                })}
                                                <li className="control-horizon" style={{marginTop: "10px"}}>
                                                    <button className="btn-tertiary btn-icon-left js-btn-add-time" onClick={function(){clickAddTimeRow(index)}} disabled={editControlsDisabled}><span className="icon material-icons-round md-18">add_circle_outline</span>時間枠追加</button>
                                                </li>
                                            </ul>
                                        </div>
                                        <div className="schedule-detail-item-control">
                                            {rowRef.length > 1 &&
                                                <button className="btn-row js-btn-del-day"　onClick={function(){clickDelRow(index)}} disabled={editControlsDisabled}><span className="material-icons-round">clear</span></button>
                                            }
                                        </div>
                                    </div>                     
                                )
                            })}
                            <div className="form-row">
                                {rowRef.length < 8 &&
                                    <button className="btn-tertiary btn-icon-left js-btn-add-day" onClick={clickAddRow} disabled={editControlsDisabled}><span className="icon material-icons-round md-18">add_circle_outline</span>枠追加</button>
                                }
                            </div>
                        </div>
                    </div>
                    <div className="control-horizon control-between margin-tb-20">
                        <button className="btn-primary" onClick={clickSave} disabled={submitDisabled}>変更を保存</button>
                        {params && <button className="btn-tertiary" onClick={clickOpen} disabled={editControlsDisabled}>削除</button>}
                        <Dialog isShow = {isShow} setIsShow = {setIsShow} callback={clickDelete} msg = {"このスケジュールを削除してもよろしいですか？"}/>
                    </div>
                </div>
            </div>
        </AdminFrame>
    )
}